import { useQuery } from '@tanstack/react-query'
import { fetchPayments, type Payment } from '@/lib/api'
import { Card } from '@/components/Surface'
import { SubPage } from '@/components/SubPage'
import { motion } from 'motion/react'
import { useMemo } from 'react'

const METHOD_LABEL: Record<Payment['method'], string> = {
  sbp: 'СБП',
  stars: 'Stars ⭐',
  crypto: 'Крипта',
}

const STATUS_LABEL: Record<Payment['status'], string> = {
  pending: 'в обработке',
  paid: 'оплачено',
  failed: 'отклонено',
  refunded: 'возврат',
}

const STATUS_COLOR: Record<Payment['status'], { fg: string; bg: string }> = {
  pending: { fg: 'var(--st-pending-fg)', bg: 'var(--st-pending-bg)' },
  paid: { fg: 'var(--st-paid-fg)', bg: 'var(--st-paid-bg)' },
  failed: { fg: 'var(--st-failed-fg)', bg: 'var(--st-failed-bg)' },
  refunded: { fg: 'var(--st-refunded-fg)', bg: 'var(--st-refunded-bg)' },
}

export function Payments() {
  const { data } = useQuery({ queryKey: ['payments'], queryFn: fetchPayments })

  const grouped = useMemo(() => groupByMonth(data ?? []), [data])
  const totalSpent = useMemo(
    () =>
      (data ?? [])
        .filter(p => p.status === 'paid')
        .reduce((sum, p) => sum + p.amount_rub, 0),
    [data],
  )

  return (
    <SubPage title="история" subtitle="все платежи и чеки">
      <div className="px-5 space-y-3 stagger">
        {/* Total */}
        <Card className="p-5" style={{ ['--i' as never]: 0 }}>
          <div className="text-[13px] text-[var(--color-muted)]">всего потрачено</div>
          <div className="display text-[36px] mt-2 num">
            {totalSpent}
            <span className="text-[var(--color-dim)] text-[18px] ml-2 font-semibold">₽</span>
          </div>
          <div className="text-[12px] text-[var(--color-muted)] mt-1">
            за {data?.length ?? 0} плат{decline(data?.length ?? 0, 'ёж', 'ежа', 'ежей')}
          </div>
        </Card>

        {grouped.map((group, gi) => (
          <div key={group.label} style={{ ['--i' as never]: gi + 1 }}>
            <h2 className="text-[12px] text-[var(--color-muted)] uppercase tracking-wider px-1 mb-2 mt-2">
              {group.label}
            </h2>
            <Card className="overflow-hidden">
              {group.items.map((p, i, arr) => (
                <PaymentRow
                  key={p.id}
                  payment={p}
                  divider={i < arr.length - 1}
                  index={i}
                />
              ))}
            </Card>
          </div>
        ))}

        {(!data || data.length === 0) && (
          <Card className="p-8 text-center" style={{ ['--i' as never]: 1 }}>
            <div className="text-[14px] text-[var(--color-muted)]">пока нет платежей</div>
          </Card>
        )}
      </div>
    </SubPage>
  )
}

function PaymentRow({
  payment,
  divider,
  index,
}: {
  payment: Payment
  divider: boolean
  index: number
}) {
  const status = STATUS_COLOR[payment.status]
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 + index * 0.035 }}
      className="px-5 py-4 flex items-center gap-3"
      style={{ borderBottom: divider ? '1px solid var(--color-line)' : 'none' }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[14.5px] font-semibold">
          {payment.plan_name.toLowerCase()}
          <span className="text-[var(--color-dim)] font-normal text-[12.5px] ml-2 num">#{payment.id}</span>
        </div>
        <div className="num text-[12px] text-[var(--color-muted)] mt-0.5">
          {formatDate(payment.created_at)} · {METHOD_LABEL[payment.method]}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="num text-[14.5px] font-semibold">
          {payment.status === 'failed' || payment.status === 'refunded' ? '−' : ''}
          {payment.amount_rub} ₽
        </div>
        <div
          className="inline-block text-[10.5px] font-medium px-1.5 py-0.5 rounded-md mt-1"
          style={{ background: status.bg, color: status.fg }}
        >
          {STATUS_LABEL[payment.status]}
        </div>
      </div>
    </motion.div>
  )
}

function groupByMonth(payments: Payment[]): { label: string; items: Payment[] }[] {
  const groups = new Map<string, Payment[]>()
  for (const p of payments) {
    const d = new Date(p.created_at)
    const key = `${d.getFullYear()}-${d.getMonth()}`
    const arr = groups.get(key) ?? []
    arr.push(p)
    groups.set(key, arr)
  }
  return Array.from(groups.values()).map(items => {
    const d = new Date(items[0].created_at)
    const label = d
      .toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
      .replace(' г.', '')
    return { label, items }
  })
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

function decline(n: number, one: string, few: string, many: string) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}
